import { Card, ETextStyleVariant, SkeletonLine, Text } from '@outdoorsyco/bonfire';
import React from 'react';

import { ProBadge } from '@/components/common/ProBadge';
import { trackFeatureViewedEvent } from '@/services/analytics/features';
import { EFeatureName, EFeatureSource } from '@/services/analytics/features/types';
import generateTestIdFrom from '@/utility/generateTestIdFrom';
import { isAutoRentalTile, TAutoRentalTile } from '@/utility/mapAutoSearchResultToTile';
import { isCampgroundTile, ISearchResultTile } from '@/utility/mapSearchResultToTile';
import { formatMeasurement } from '@/utility/measurements';
import { mapPluralUnitToSingular } from '@/utility/units';

import { ListingTilePrice } from './ListingTilePrice';
import { ListingTileRating } from './ListingTileRating';
import { ListingTileTotalPrice } from './ListingTileTotalPrice';
import { computeCustomListingTitle } from './utils';

type TListingTileContentProps = {
  listingTile: ISearchResultTile | TAutoRentalTile;
  showProBadge?: boolean;
  customTitle?: string;
  secondaryInfo?: React.ReactNode;
  isEscapesSearchPage?: boolean;
};

export const ListingTileContent = ({
  listingTile,
  showProBadge,
  customTitle,
  secondaryInfo,
  isEscapesSearchPage,
}: TListingTileContentProps) => {
  const firstLineItems = (() => {
    if (isAutoRentalTile(listingTile)) {
      // For auto rentals: show Seatbelts {#} Fuel type
      return [listingTile.details?.seatbelts, listingTile.details?.fuelType];
    }

    if (isCampgroundTile(listingTile)) {
      return [listingTile.sitesDisplayText];
    }

    if (listingTile.isStay) {
      return [
        listingTile.details?.classText,
        listingTile.details?.sleeps,
        listingTile.details?.beds,
        listingTile.details?.maxVehicleLength,
      ];
    }

    // For regular rentals: show Sleeps ft
    return [listingTile.details?.sleeps, listingTile.details?.feet];
  })().filter(Boolean);

  const secondLineItems = (() => {
    if (!isAutoRentalTile(listingTile) && isCampgroundTile(listingTile)) {
      return [listingTile.details?.location];
    }

    if (!isAutoRentalTile(listingTile) && listingTile.isStay) {
      return [listingTile.details?.location];
    }

    // For regular rentals: show City Distance
    return [
      listingTile.details?.location,
      listingTile.details?.distance &&
        formatMeasurement(listingTile.details.distance, {
          unit: mapPluralUnitToSingular(listingTile.details.distanceUnit || 'miles'),
          long: true,
          formatOptions: {
            notation: 'compact',
            maximumFractionDigits: 1,
          },
        }),
    ];
  })().filter(Boolean);

  return (
    <Card.Content className="w-full">
      <div className="flex gap-2">
        <div className="flex-grow">
          {(listingTile.loading && (
            <Text component="div" variant={ETextStyleVariant.MediumBold}>
              <SkeletonLine width="80%" className="bg-gray-200" />
            </Text>
          )) || (
            <Text component="div" variant={ETextStyleVariant.MediumBold} className="line-clamp-2">
              {showProBadge && listingTile.dealer && (
                // Prevent opening the tile when clicking on the badge
                // eslint-disable-next-line
                <span
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  className="mr-2 align-middle">
                  <ProBadge
                    width={24}
                    height={24}
                    // Temporarily remove the title to prevent the browser's default tooltip
                    onMouseEnter={e => {
                      const card = e.currentTarget.closest('a');

                      if (card) {
                        card.setAttribute('data-original-title', card?.getAttribute('title') ?? '');
                        card.removeAttribute('title');
                      }

                      trackFeatureViewedEvent({
                        rentalID: listingTile.id,
                        source: EFeatureSource.LISTING_TILE,
                        featureName: EFeatureName.PRO_BADGE,
                      });
                    }}
                    // Restore the title when the badge tooltip is no longer shown
                    onMouseLeave={e => {
                      const card = e.currentTarget.closest('a');

                      if (card) {
                        card.setAttribute('title', card?.getAttribute('data-original-title') ?? '');
                        card.removeAttribute('data-original-title');
                      }
                    }}
                    // Use negative margin so it does not increase the height of the title
                    className="inline-block -my-0.5"
                  />
                </span>
              )}

              {customTitle || computeCustomListingTitle(listingTile) || listingTile.title}
            </Text>
          )}
        </div>

        <div className="flex items-baseline">
          {(listingTile.loading && (
            <Text component="div" variant={ETextStyleVariant.MediumBold}>
              <SkeletonLine width="50px" className="bg-gray-200" />
            </Text>
          )) ||
            (!!listingTile.details?.ratingNumber && (
              <ListingTileRating
                rating={listingTile.details.ratingNumber}
                quantity={listingTile.details.ratingQtd}
              />
            ))}
        </div>
      </div>

      {secondaryInfo || (
        <>
          {(listingTile.loading && (
            <Text component="div" variant={ETextStyleVariant.MediumRegular}>
              <SkeletonLine width="60%" className="bg-gray-200" />
            </Text>
          )) ||
            (firstLineItems.length > 0 && (
              <Text
                component="div"
                variant={ETextStyleVariant.MediumRegular}
                className="text-gray-50"
                data-testid={generateTestIdFrom(listingTile.title || '', 'listing-info-1st-line')}>
                {firstLineItems.join(' • ')}
              </Text>
            ))}

          {(listingTile.loading && (
            <Text component="div" variant={ETextStyleVariant.MediumRegular}>
              <SkeletonLine width="40%" className="bg-gray-200" />
            </Text>
          )) ||
            (secondLineItems.length > 0 && (
              <Text
                component="div"
                variant={ETextStyleVariant.MediumRegular}
                className="text-gray-50"
                data-testid={generateTestIdFrom(listingTile.title || '', 'listing-info-2nd-line')}>
                {secondLineItems.join(' • ')}
              </Text>
            ))}
        </>
      )}

      {(listingTile.loading && (
        <Text component="div" variant={ETextStyleVariant.MediumBold}>
          <SkeletonLine width="50%" className="mt-2 bg-gray-200" />
        </Text>
      )) ||
        (() => {
          if (!listingTile.price) return null;

          if (!isAutoRentalTile(listingTile) && isCampgroundTile(listingTile)) {
            return (
              <ListingTilePrice
                {...listingTile.price}
                className="mt-2"
                isEscapesSearchPage={isEscapesSearchPage}
              />
            );
          }

          if (listingTile.price?.estimatedPriceBreakdown?.total) {
            return <ListingTileTotalPrice listingTile={listingTile} className="mt-2" />;
          }

          return <ListingTilePrice {...listingTile.price} className="mt-2" />;
        })()}
    </Card.Content>
  );
};
